/**
 * /segments/:segmentId — edit a saved segment. Same UI as /new, just
 * with hydration + PATCH on save.
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import {
  EMPTY_SEGMENT,
  type FieldDescriptor,
  type Segment,
} from "../../../lib/segments-dsl.js";
import { SegmentBuilder, useSegmentPreview } from "../../../components/segment-builder.js";
import { PreviewBadge } from "./new.js";

export const Route = createFileRoute("/_app/segments/$segmentId")({
  component: EditSegmentPage,
});

interface SegmentRow {
  id: string;
  name: string;
  description: string | null;
  filter: Segment;
  createdAt: string;
  updatedAt: string;
}

function EditSegmentPage() {
  const { segmentId } = Route.useParams();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [filter, setFilter] = useState<Segment>(EMPTY_SEGMENT);
  const [fields, setFields] = useState<FieldDescriptor[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const preview = useSegmentPreview(filter);

  useEffect(() => {
    void load();
    api
      .get<{ fields: FieldDescriptor[] }>("/segments/fields")
      .then((r) => setFields(r.data.fields))
      .catch(() => undefined);
  }, [segmentId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<{ segment: SegmentRow }>(`/segments/${segmentId}`);
      const s = r.data.segment;
      setName(s.name);
      setDescription(s.description ?? "");
      setFilter(s.filter);
      setSavedAt(s.updatedAt);
    } catch {
      setError("Segment not found");
    } finally {
      setLoading(false);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.patch<{ segment: SegmentRow }>(`/segments/${segmentId}`, {
        name: name.trim(),
        description: description.trim() || null,
        filter,
      });
      setSavedAt(r.data.segment.updatedAt);
    } catch (err) {
      const data = (err as { response?: { data?: { message?: string; errors?: { path: string; message: string }[] } } })
        .response?.data;
      const detail = data?.errors?.[0] ? ` (${data.errors[0].path}: ${data.errors[0].message})` : "";
      setError((data?.message ?? "Save failed") + detail);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p>Loading…</p>;

  return (
    <section style={{ maxWidth: 880, margin: "0 auto" }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 28 }}>Edit segment</h1>
        <Link
          to="/contacts"
          search={{ segmentId }}
          className="brut-button is-secondary"
          style={{ fontSize: 14, padding: "8px 12px" }}
        >
          View contacts →
        </Link>
      </header>

      <form onSubmit={save} style={{ display: "grid", gap: 14 }}>
        <div className="brut-card" style={{ padding: 14, display: "grid", gap: 10 }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Segment name"
            className="brut-input"
            maxLength={120}
            required
          />
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            className="brut-input"
            maxLength={1024}
          />
          {savedAt ? (
            <div style={{ color: "var(--muted)", fontSize: 12 }}>
              Last saved {new Date(savedAt).toLocaleString()}
            </div>
          ) : null}
        </div>

        <SegmentBuilder value={filter} fields={fields} onChange={setFilter} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
          }}
        >
          <PreviewBadge {...preview} />
          {error ? (
            <div style={{ color: "#b40000", fontSize: 13, flex: 1 }}>{error}</div>
          ) : null}
          <button type="submit" disabled={!name.trim() || busy} className="brut-button">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </section>
  );
}
